/**
 * Centralized Pricing Cache Service - Countdown Edition
 * All reads/writes to the card_prices table go through this file.
 * No other file should touch card_prices directly.
 *
 * Data source: Countdown API (eBay sold listings)
 * Storage: evidence[] (raw listings) + stats{} (computed per condition/grade)
 */

import { supabase } from '../lib/supabase';
import { fetchPricing, fetchGradedPricing } from '../lib/fetchPricing';
import {
  salesForTier, priceFrom as priceFromSales, headlinePrice, DEFAULT_PREFS,
} from '../utils/priceMath';
import { FAILURE_STAGES } from '../utils/pricingReports';
import { computeHeadlineMap, headlineOrEstimate, estimateRawTier, tierAverage, RAW_TIERS } from '../utils/headlineMap';
import { sourceForSet, EBAY, TCG } from './priceSource';

const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ---- Helpers ----

// ---- Condition normalisation ----
//
// Three eras of rows coexist in card_prices and none of them will be rewritten:
//
//   Countdown short keys    { nm, lp, mp, hp, dmg, all, raw }
//   Poketrace human labels  { "Near Mint", "Lightly Played", ... }
//   graded, either era      { "PSA 10", "CGC 9.5", ... }
//
// So the reader normalises rather than the writer. Every condition lookup in
// this file goes through the helpers below.

const CONDITION_SYNONYMS = {
  nm: ['nm', 'm', 'mint', 'near mint', 'nearmint', 'near_mint'],
  lp: ['lp', 'ex', 'excellent', 'lightly played', 'lightlyplayed', 'lightly_played'],
  mp: ['mp', 'gd', 'good', 'moderately played', 'moderatelyplayed', 'moderately_played'],
  hp: ['hp', 'poor', 'played', 'heavily played', 'heavilyplayed', 'heavily_played'],
  dmg: ['dmg', 'damage', 'damaged'],
};

const CONDITION_LOOKUP = new Map(
  Object.entries(CONDITION_SYNONYMS).flatMap(([key, words]) => words.map(word => [word, key]))
);

function conditionToKey(grade) {
  if (!grade) return 'nm';
  return CONDITION_LOOKUP.get(String(grade).trim().toLowerCase()) || 'nm';
}

/** "psa10", "PSA 10" and "psa_10" all address the same tier. */
function normaliseGradeKey(grade) {
  return String(grade || '')
    .trim()
    .toUpperCase()
    .replace(/[_\s]+/g, ' ')
    .replace(/^([A-Z]+)(?=\d)/, '$1 ')
    .trim();
}

/**
 * Check if a grade string represents a graded card (e.g. "PSA 10", "CGC 9.5")
 */
function isGradedCard(grade) {
  if (!grade) return false;
  const g = grade.trim().toUpperCase();
  return /^(PSA|CGC|BGS|SGC|ACE|TAG|GMA|MNT)\s/.test(g);
}

export function formatPrice(price) {
  if (price == null) return null;
  if (price < 1) return `$${price.toFixed(2)}`;
  const rounded = Math.round(price * 100) / 100;
  return `$${rounded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Should this card be looked up again?
 *
 * A failed lookup leaves a row behind carrying retry_after, so a card that
 * cannot be priced stops being re-attempted on every screen open. Rows that
 * predate negative caching have no schedule, so they get one attempt, which
 * then gives them one.
 */
export function isDueForRetry(row, price) {
  if (!row) return true;                          // never looked up
  if (price != null && price > 0) return false;   // already has a usable price
  if (!row.retry_after) return true;              // no price and no schedule yet
  return new Date(row.retry_after).getTime() <= Date.now();
}

/**
 * A row that exists, holds nothing, and never recorded why.
 *
 * These are the cards that looked like they were never even searched. Something
 * created the row - a warm, an interrupted fetch - and left it empty with no
 * failure stage, so nothing counted it as a failure and nothing tried again. It
 * simply showed no price, for ever.
 */
export function isHollow(row) {
  if (!row) return false;
  // A stored headline is the whole point of the row, so having one settles it.
  // Evidence is only consulted for rows the grid no longer reads it for, which
  // is every row written before the headline column existed.
  if (row.headline && Object.keys(row.headline).length > 0) return false;
  const sales = Array.isArray(row.evidence) ? row.evidence.length : 0;
  // Tiers without sales counts as hollow now.
  //
  // It did not used to: a row carrying the vendor's aggregate produced a price,
  // so it was doing its job. Since a price has to be backed by sales you can
  // open, that same row shows nothing, and the test that decides whether to try
  // again was still calling it healthy. Seven cards sat like that from late
  // August - tiers, no sales, no failure stage, so nothing counted them as
  // failed and nothing ever refetched them, while the vendor had twenty
  // listings waiting for each one.
  return sales === 0 && !row.last_failure_stage;
}

/**
 * Does this card still owe us a lookup?
 *
 * The screen and the fetcher each used to decide this for themselves, and they
 * did not agree: the screen asked "due for retry OR hollow", the fetcher asked
 * only "due for retry". A hollow row carrying a future retry_after was picked
 * by one and dropped by the other, so it never healed and was picked again on
 * every single open. One predicate, both callers.
 */
export function needsPricing(row, price) {
  return isDueForRetry(row, price) || isHollow(row);
}

/**
 * Remember that a lookup failed, and when it is worth trying again.
 *
 * Deliberately does not touch stats or evidence: the row key is the printing,
 * so a graded copy and a raw copy share one row, and a graded lookup that finds
 * nothing must not erase working raw prices.
 */
export async function recordPricingFailure({ cardName, setName, setNumber, variant, stage }) {
  if (!cardName) return;
  try {
    const { error } = await supabase.rpc('pricing_record_failure', {
      p_card_name: cardName,
      p_set_name: setName || '',
      p_set_number: setNumber || '',
      p_variant: variant || '',
      p_stage: stage || null,
    });
    if (error) console.warn('[PriceCache] could not record failure:', error.message);
  } catch (err) {
    console.warn('[PriceCache] failure record threw:', err?.message);
  }
}

// ---- Read Functions ----

/**
 * The one identity a cached price is filed under.
 *
 * The number is trimmed at the slash because the two halves of this app
 * disagree about it: the catalogue says "225", the printed card says "225/193",
 * and both reach here. Everything else is trimmed and lowercased so a
 * difference of case never costs a cache hit.
 */
export function priceKey({ name, setName, setNumber, variant }) {
  const clean = (v) => String(v ?? '').trim().toLowerCase();
  return [
    clean(name),
    clean(setName),
    clean(setNumber).split('/')[0],
    clean(variant),
  ].join('||');
}


/**
 * One card's row, built from every row the vendors wrote for it.
 *
 * A card is one thing to a vendor at a counter and several rows in here: the
 * scanner files a finish ("holo"), the price vendors often do not, and the
 * number is written both bare ("182") and printed ("182/226"). So the row for
 * the exact printing can be empty while the row beside it holds everything.
 * Mega Kangaskhan ex is the case that produced this: 200 sales under "normal",
 * a $60.24 TCGplayer ask under "holo", and a screen that said TCGplayer had
 * nothing.
 *
 * The exact printing always leads. Where it is silent - no sales, no asks - the
 * sibling's figure stands in, and `borrowed` names which parts came from
 * somewhere else so the screen can mark them as an estimate rather than quote
 * another printing as fact.
 *
 * @param {object[]} rows every card_prices row in hand for this name
 * @param {{setName?: string, setNumber?: string, variant?: string}} want
 * @returns {object|null} a row to price from, or null when nothing matches
 */
export function pickPriceRow(rows, want = {}) {
  const clean = (v) => String(v ?? '').trim().toLowerCase();
  const wantNum = clean(want.setNumber).split('/')[0];
  const wantSet = clean(want.setName);
  const wantVar = clean(want.variant);
  const sales = (r) => (Array.isArray(r?.evidence) ? r.evidence.length : 0);
  const hasAsks = (r) => Boolean(r?.asks && Object.keys(r.asks).length);
  const knows = (r) => sales(r) > 0 || hasAsks(r) || Boolean(r?.stats);

  let sameCard = (rows || []).filter((r) => {
    const num = clean(r.set_number).split('/')[0];
    return !wantNum || !num || num === wantNum;
  });
  if (!sameCard.length) return null;

  // Alakazam is #1 in Base Set and #1 in Base Set 2, at very different prices.
  // When any row names the set asked for, the others are not this card.
  const sameSet = wantSet ? sameCard.filter((r) => clean(r.set_name) === wantSet) : [];
  if (sameSet.length) sameCard = sameSet;

  const exact = sameCard.filter((r) => clean(r.variant) === wantVar);
  const siblings = sameCard
    .filter((r) => !exact.includes(r))
    .sort((a, b) => (sales(b) + (hasAsks(b) ? 1 : 0)) - (sales(a) + (hasAsks(a) ? 1 : 0)));

  // A printing that knows nothing at all does not stand in front of one that
  // does: the sibling's row is simply the answer, as it has always been. Only
  // a printing that knows SOMETHING gets its gaps filled, because then the row
  // really is about the card in hand.
  //
  // A caller that names no printing - the card screen opening a catalogue card
  // - is asking about the card rather than one of its finishes, so everything
  // known about it is fair to gather, marked as borrowed either way.
  const exactKnowing = exact.find(knows);
  const anyKnowing = siblings.find(knows) || exact.find(knows);
  const base = wantVar ? (exactKnowing || anyKnowing) : (anyKnowing || exact[0] || sameCard[0]);
  const composing = Boolean(base) && (!wantVar || base === exactKnowing);
  if (!base) return exact[0] || sameCard[0] || null;
  if (!composing) return base;

  // Anything the chosen row does not know, taken from the printing beside it.
  const borrowed = {};
  const filled = { ...base };
  const lend = (field, from) => {
    filled[field] = from[field];
    borrowed[field] = from.variant || 'another printing';
  };
  for (const other of [...exact, ...siblings]) {
    if (other === base) continue;
    if (!sales(filled) && sales(other)) lend('evidence', other);
    if (!filled.stats && other.stats) lend('stats', other);
    if (!hasAsks(filled) && hasAsks(other)) lend('asks', other);
  }
  if (!Object.keys(borrowed).length) return base;
  filled._borrowed = borrowed;
  return filled;
}

/**
 * Bulk read card prices from card_prices table.
 * Computes display price based on user's pricing preferences.
 * @param {Array} cards - [{id, name, set_name, set_number, grade, variant}]
 * @param {Object} [pricingPrefs] - users.pricing_prefs. Carries source_cut_set_id,
 *   which decides whether a card prices off eBay sold or TCGplayer first. Null
 *   is fine and usual: priceSource falls back to the prefs the active profile
 *   published, and then to the default cut.
 * @returns {Map<cardId, {row, price, estimated, source, formatted}>}
 */
const NAME_CHUNK = 150;
// With a number filter the request carries about three terms per card, so
// the chunk shrinks to keep the URL well inside what the gateway accepts.
const NARROW_CHUNK = 40;

/**
 * A PostgREST `or` filter that keeps only rows for these card numbers.
 *
 * Rows are written under the bare number ("173") by some call sites and the
 * printed form ("173/226") by others, and a few have no number at all, so all
 * three shapes pass. A number with characters the filter grammar cannot carry
 * disables narrowing for the whole chunk rather than risk dropping a card.
 *
 * @returns {string|null} the filter, or null when the rows cannot be narrowed
 */
export function numberFilter(setNumbers) {
  const bare = [...new Set((setNumbers || [])
    .map((n) => String(n ?? '').trim().toLowerCase().split('/')[0])
    .filter(Boolean))];
  if (!bare.length) return null;
  if (bare.some((n) => !/^[a-z0-9._-]+$/.test(n))) return null;
  const terms = [`set_number.is.null`];
  // The row's number is matched without regard to case, on both shapes.
  bare.forEach((n) => { terms.push(`set_number.ilike.${n}`, `set_number.ilike.${n}/%`); });
  return terms.join(',');
}

/**
 * Prices for a list of cards, read as cheaply as the screen allows.
 *
 * Two things used to make this unusable at any real size. It selected every
 * column, and `evidence` - the full sales record behind each price - is 88% of
 * the weight of the table, fetched only so the phone could rederive a number
 * already known when the row was written. And it put every card name into one
 * `in(...)`, which is a query string: a shop with a few thousand cards would
 * have built a request too long to send.
 *
 * So the headline is read, not recomputed, and names go in chunks.
 */
export async function getBulkCardPrices(cards, pricingPrefs, { detailed = false, withStats = false } = {}) {
  if (!cards || cards.length === 0) return new Map();

  const uniqueNames = [...new Set(cards.map(c => c.name).filter(Boolean))];
  if (uniqueNames.length === 0) return new Map();

  // Everything the grid needs and nothing it does not. `asks` stays because it
  // is small - about seventy bytes a row - and still backs a condition with no
  // sales behind it on rows written before the headline existed.
  // `detailed` is for screens that go on to show the sales themselves - the
  // bulk scanner's breakdown sheet - and never for a grid.
  // `withStats` adds the per-condition ladder, a couple of hundred bytes a
  // row, for a screen that lets you tap between conditions without reopening
  // the sales record behind them. The sales themselves stay behind until a
  // breakdown sheet asks for one row's worth (see loadPriceEvidence).
  const LIGHT = 'id,card_name,set_name,set_number,variant,headline,priced_at,asks,'
    + 'last_refreshed_at,last_failure_stage,retry_after,refresh_due,read_count,last_read_at';
  const COLUMNS = detailed ? '*' : withStats ? `${LIGHT},stats,is_graded` : LIGHT;

  // Only rows for these cards' numbers, not every card that shares a name.
  // Nine Base Set holos pulled 138 rows and four megabytes of sales history
  // this way, over a phone connection, before one price could show. With the
  // number in the filter the same read is a dozen rows.
  const narrow = numberFilter(cards.map((c) => c.set_number));
  const chunk = narrow ? NARROW_CHUNK : NAME_CHUNK;

  const rows = [];
  for (let i = 0; i < uniqueNames.length; i += chunk) {
    let query = supabase
      .from('card_prices')
      .select(COLUMNS)
      .in('card_name', uniqueNames.slice(i, i + chunk));
    if (narrow) query = query.or(narrow);
    const { data, error } = await query;

    if (error) {
      console.error('[PriceCache] Bulk read error:', error);
      return new Map();
    }
    rows.push(...(data || []));
  }

  console.log('[PriceCache] Bulk read:', uniqueNames.length, 'names ->', rows.length, 'rows');

  // Both sides through the same normaliser.
  //
  // They were not: rows were indexed on their raw columns while lookups
  // lowercased only the variant. So a row written under "225" was invisible to
  // a lookup for "225/193", and any difference of case in a name or a set name
  // missed outright. A price fetched seconds earlier would not be found, and
  // the screen went back to the vendor for something it already had.
  // Grouped by the card, not by the exact printing. A lookup for the "holo"
  // row used to find nothing when the vendor filed the card as "normal", and
  // the card read as unpriced on the grid, in a deal and on a label - about
  // 1,500 cards in this catalogue. pickPriceRow below decides which of a
  // card's rows to price from.
  const rowsByCard = new Map();
  for (const row of rows) {
    const cardKey = priceKey({
      name: row.card_name,
      setName: row.set_name,
      setNumber: row.set_number,
      variant: '',
    });
    const list = rowsByCard.get(cardKey);
    if (list) list.push(row); else rowsByCard.set(cardKey, [row]);
  }

  const results = new Map();
  for (const card of cards) {
    const key = priceKey({
      name: card.name,
      setName: card.set_name,
      setNumber: card.set_number,
      variant: '',
    });
    const row = pickPriceRow(rowsByCard.get(key) || [], {
      setName: card.set_name,
      setNumber: card.set_number,
      variant: card.variant,
    });
    if (row) {
      const detail = detailed
        ? priceDetailForCard(row.stats, card.grade, card.set_name, pricingPrefs, row.evidence, null, row.asks)
        : storedPriceDetail(row, card.grade, pricingPrefs);
      const price = detail?.value ?? null;
      results.set(card.id, {
        row,
        price,
        estimated: Boolean(detail?.estimated),
        source: detail?.source ?? null,
        formatted: price != null ? formatPrice(price) : null,
      });
    }
  }

  return results;
}

/**
 * The sales behind one price row, fetched when a breakdown sheet opens.
 *
 * The pricing screen reads rows without their sales so nine cards cost
 * kilobytes rather than megabytes. The sheet that lists the sales asks for
 * them here, one row at a time, and only for the card that was tapped.
 *
 * @returns {Promise<object>} the row with `evidence` filled in; the row as
 *   given if the read fails, so the sheet still opens on the ladder it has
 */
export async function loadPriceEvidence(row) {
  if (!row?.id || Array.isArray(row.evidence)) return row;
  const { data, error } = await supabase
    .from('card_prices')
    .select('evidence')
    .eq('id', row.id)
    .maybeSingle();
  if (error || !data) return row;
  return { ...row, evidence: Array.isArray(data.evidence) ? data.evidence : [] };
}

/**
 * A price read straight off a row already in hand.
 *
 * The pricing screen used to re-read the table by exact key after the gate
 * had already handed it the row. The exact key and the gate's fuzzy match do
 * not always agree - a row warmed under "reverse" is the same card the review
 * calls "Reverse Holo" - so the second read missed and a priced card showed no
 * price. Reading the row it was given cannot miss.
 */
export function priceFromRow(row, grade, pricingPrefs = null) {
  if (!row) return null;
  return priceDetailForCard(row.stats, grade, row.set_name, pricingPrefs, row.evidence, null, row.asks);
}

/**
 * The stored headline for one card, by the grade that card is in.
 *
 * Falls back to the asking price for a raw condition no sale covers, which is
 * what the grid did itself before the headline was stored, so rows written
 * before this column existed still price the same way.
 */
export function storedPriceFor(row, grade, pricingPrefs = null) {
  return storedPriceDetail(row, grade, pricingPrefs)?.value ?? null;
}

/**
 * The same, saying whether the number is the card's own or read off its
 * neighbouring conditions, and which market it came from.
 *
 * A raw condition no sale, average or ask covers is estimated from the
 * conditions that are, because a screen full of cards has to have an answer
 * for every one of them. Never for a slab.
 *
 * The account's cut decides which of the two is asked first (see priceSource).
 * It only decides the order: a preferred source with nothing for this
 * condition hands straight over to the other one, and then to the estimate, so
 * a row with any data at all still produces a number.
 *
 * Slabs are eBay only. TCGplayer asks in this app are raw conditions; there is
 * no ask for a PSA 10 to prefer.
 */
export function storedPriceDetail(row, grade, pricingPrefs = null) {
  const graded = isGradedCard(grade);
  const gradeKeyOf = (g) => normaliseGradeKey(g).replace(/[\s.]/g, '_');
  const stored = headlineOrEstimate(row?.headline, grade, gradeKeyOf, conditionToKey, graded);
  if (graded) return stored ? { ...stored, source: EBAY } : stored;

  // The headline is written from sales first, so a non-estimated one is the
  // sold answer for this condition.
  // A figure taken off the printing beside this one is a fair guide and not
  // this card's own number, so it carries the estimate mark wherever it lands.
  // See pickPriceRow for why a card's rows get split in the first place.
  const borrowed = row?._borrowed || null;
  const soldBorrowed = Boolean(borrowed && (borrowed.evidence || borrowed.stats));
  const askBorrowed = Boolean(borrowed && borrowed.asks);

  const sold = stored && !stored.estimated
    ? { ...stored, estimated: soldBorrowed, source: EBAY, from: soldBorrowed ? borrowed.evidence || borrowed.stats : null }
    : null;
  const askValue = row?.asks?.[conditionToKey(grade)]?.price;
  const ask = typeof askValue === 'number' && askValue > 0
    ? { value: askValue, estimated: askBorrowed, source: TCG, from: askBorrowed ? borrowed.asks : null }
    : null;

  const prefer = sourceForSet(row?.set_name, pricingPrefs);
  const first = prefer === TCG ? (ask || sold) : (sold || ask);
  if (first) return first;

  // Neither market covers this condition. The estimate off the neighbouring
  // conditions is the last answer, and it is already marked as one.
  return stored ? { ...stored, source: prefer } : null;
}

/**
 * Store the TCGplayer asking prices on the card's existing price row.
 *
 * Same key as everything else, so one read fills the sold column and the asking
 * column together and a returning visit paints both at once.
 *
 * Best effort: a card that prices but cannot cache its asks still shows them.
 */
export async function cacheAsks({ cardName, setName, setNumber, variant }, asks) {
  if (!cardName || !asks) return;
  const { error } = await supabase
    .from('card_prices')
    .update({ asks, asks_at: new Date().toISOString() })
    .eq('card_name', cardName)
    .eq('set_name', setName || '')
    .eq('set_number', setNumber || '')
    .eq('variant', (variant || '').toLowerCase().trim());
  if (error) console.warn('[PriceCache] asks cache failed:', error.message);
}

/** Asking prices move faster than sold data, so they expire sooner. */
export const ASKS_TTL_HOURS = 24;

export function asksAreFresh(asksAt) {
  if (!asksAt) return false;
  const age = (Date.now() - new Date(asksAt).getTime()) / 3600000;
  return Number.isFinite(age) && age < ASKS_TTL_HOURS;
}

/**
 * Single row lookup from card_prices.
 */
export async function getCardPriceRow(cardName, setName, setNumber, variant) {
  if (!cardName) return null;

  // Fetch every row for this card name and choose in JS, rather than asking the
  // database for one exact key.
  //
  // The exact key was wrong twice over, and both are visible in the data.
  // Infernape Twilight Masquerade exists as "#173" with 160 sales AND as
  // "#173/226" with none, because different call sites write the number
  // differently. And Mega Gallade ex exists as "normal" with 200 sales and
  // "holo" with none, because the vendor does not split the finish the way the
  // scanner does. Either way the screen asked for the empty one and showed
  // nothing, on a card we had 200 sales for.
  //
  // Choosing in JS does not mean downloading everything. Fifty-one Pikachu
  // rows carry 1.8 MB of sales between them, and this ran on every identify,
  // every save and every card screen. The number filter below leaves the same
  // choice to make from the handful of rows it can actually be made from.
  const wantNum = String(setNumber ?? '').trim().toLowerCase().split('/')[0];

  let query = supabase
    .from('card_prices')
    .select('*')
    .eq('card_name', cardName);
  const narrow = numberFilter([wantNum]);
  if (narrow) query = query.or(narrow);
  const { data, error } = await query;

  if (error) {
    console.error('[PriceCache] Single read error:', error);
    return null;
  }
  if (!data?.length) return null;

  // The same choice the grid makes, made here: see pickPriceRow.
  return pickPriceRow(data, { setName, setNumber, variant });
}

/**
 * Compute display price from evidence array using user's pricing preferences.
 *
 * Modern cards (2017+): filter evidence by date range (1m/3m/6m/12m), then median.
 * Vintage cards (pre-2017): top3/low3/12mo median/all-time median.
 * Graded cards: always use stats (no date filtering needed, grade-specific).
 *
 * Falls back to precomputed stats if evidence is missing or empty.
 *
 * @param {Object} stats - Precomputed stats from card_prices row
 * @param {string} grade - Card grade/condition
 * @param {string} setName - Set name (for vintage detection)
 * @param {Object} pricingPrefs - {modern_range, vintage_method}
 * @param {Array} evidence - Raw evidence array from card_prices row
 */
/**
 * Get detailed pricing info including fallback grade for graded cards.
 * Returns { price, nearestGrade, nearestPrice, message }
 */
export function getPriceDetails(stats, grade, setName, pricingPrefs, evidence, asks) {
  const price = getPriceForCard(stats, grade, setName, pricingPrefs, evidence, null, asks);

  if (!isGradedCard(grade)) {
    if (price != null) return { price, nearestGrade: null, nearestPrice: null, message: null };
    if (!stats && (!evidence || evidence.length === 0)) return { price: null, nearestGrade: null, nearestPrice: null, message: 'No sold data found for this card' };
    return { price: null, nearestGrade: null, nearestPrice: null, message: 'No pricing available' };
  }

  // Graded card
  if (price != null) return { price, nearestGrade: null, nearestPrice: null, message: null };

  if (!stats) return { price: null, nearestGrade: null, nearestPrice: null, message: 'No sold data found' };

  // Find closest grade below
  const parts = grade.trim().split(/\s+/);
  const company = parts[0]?.toUpperCase() || '';
  const gradeNum = parseFloat(parts.slice(1).join(' ')) || 0;

  // Collect all grades for this company, sorted descending
  const companyGrades = Object.entries(stats)
    .filter(([key]) => key.toUpperCase().startsWith(company))
    .map(([key, data]) => {
      const num = parseFloat(key.replace(/[^0-9.]/g, '')) || 0;
      return { key, num, data };
    })
    .sort((a, b) => b.num - a.num);

  // Find the closest grade at or below the requested grade
  for (const g of companyGrades) {
    if (g.num <= gradeNum && g.data?.med != null) {
      return {
        price: null,
        nearestGrade: g.key,
        nearestPrice: g.data.med,
        message: `No ${grade} sales found`,
      };
    }
  }

  // Try any grade from this company
  if (companyGrades.length > 0 && companyGrades[0].data?.med != null) {
    return {
      price: null,
      nearestGrade: companyGrades[0].key,
      nearestPrice: companyGrades[0].data.med,
      message: `No ${grade} sales found`,
    };
  }

  return { price: null, nearestGrade: null, nearestPrice: null, message: `No ${company} graded sales found` };
}

export function getPriceForCard(stats, grade, setName, pricingPrefs, evidence, heldOut, asks) {
  return priceDetailForCard(stats, grade, setName, pricingPrefs, evidence, heldOut, asks)?.value ?? null;
}

/**
 * The full computation, saying whether the number is the card's own and which
 * market it came from.
 * @returns {{ value: number, estimated: boolean, source: 'ebay'|'tcg', from?: string[] } | null}
 */
export function priceDetailForCard(stats, grade, setName, pricingPrefs, evidence, heldOut, asks) {
  // Asks alone are enough to answer. They were not before, because the ask was
  // only ever a fallback behind sold data and a row with no sold data at all
  // had nothing to fall back from. A card in a set that prices off TCGplayer
  // can legitimately have asks and no sales.
  if (!stats && !(evidence && evidence.length > 0) && !asks) return null;

  // Graded cards are grade-specific and never blend with the raw tiers.
  if (isGradedCard(grade)) {
    // Real sales for this exact slab first. This branch used to read the
    // vendor's aggregate and nothing else, which is how a Fossil Dragonite PSA
    // 10 carried $13,600 in a collection while the app held zero PSA 10 sales
    // for it - the highest sale on the whole card was a CGC 10 at $975.
    const own = priceFromSales(
      // "PSA 10" -> "PSA_10", "CGC 9.5" -> "CGC_9_5", which is how `tierOf`
      // keys a slab.
      salesForTier(evidence, normaliseGradeKey(grade).replace(/[\s.]/g, '_')),
      DEFAULT_PREFS,
      // Reported and personally excluded sales leave the tile too. Without this
      // an exclude corrected the ladder and left the collection value alone,
      // which reads as the app ignoring the tap.
      heldOut,
    );
    if (own.value != null) return { value: own.value, estimated: false, source: EBAY };

    // Nothing backs this grade. A number with no sale behind it is worse than
    // no number, so the card prices as unknown rather than as the vendor's
    // guess.
    return null;
  }

  const wanted = conditionToKey(grade);
  // Which market answers first for this card's set. It decides the order the
  // two are tried in and nothing else: whichever one actually has a number for
  // this condition is the number shown. See services/priceSource.
  const prefer = sourceForSet(setName, pricingPrefs);

  // eBay's answer for one condition: what the last three sold for, and the
  // vendor's own average for that exact condition behind it.
  //
  // `headlinePrice` is the same maths the ladder runs, so the tile and the card
  // agree. These were two different rules once. A Team Rocket Squirtle showed
  // $5.17 on its tile and $2.96 on its own screen, and both were correct for
  // what they were asked: $5.17 is the average of the three highest near mint
  // sales of the last twelve months, $2.96 the average of the three most
  // recent. They share one sale out of six. On a vintage collection that gap
  // compounds on every card, and only one of the two numbers is the one the
  // card screen explains to you, in the caption directly above the rows.
  // headlinePrice also applies the reports and excludes, which the vintage and
  // modern helpers never knew about.
  //
  // The average behind it is the same rule the stored headline follows;
  // refusing it here while the grid used it was how a card read "no price" on
  // one screen and a number on the next.
  const soldFor = (shortKey) => {
    const sold = headlinePrice(evidence, tierKeyFor(shortKey), heldOut);
    if (sold.value != null) return sold.value;
    return tierAverage(stats, shortKey);
  };

  // TCGplayer's answer. It is not a sale and it is a different question - what
  // somebody wants for one, rather than what one went for - but it is a real
  // number about the right card, and the ladder shows it in its own column
  // beside the sold price.
  const askFor = (shortKey) => {
    const ask = asks?.[shortKey]?.price;
    return typeof ask === 'number' && ask > 0 ? ask : null;
  };

  const order = prefer === TCG ? [TCG, EBAY] : [EBAY, TCG];
  const valueFrom = (source, shortKey) => (source === TCG ? askFor(shortKey) : soldFor(shortKey));

  for (const source of order) {
    const value = valueFrom(source, wanted);
    if (value != null) return { value, estimated: false, source };
  }

  // Neither market covers this condition. Read it off the conditions that do
  // have a number, so the screen has an answer and says it is an estimate. Each
  // neighbouring condition is filled from the preferred source first, the same
  // way the asked-for one would have been.
  const byTier = {};
  for (const [shortKey, tierKey] of Object.entries(RAW_TIERS)) {
    for (const source of order) {
      const value = valueFrom(source, shortKey);
      if (value != null) { byTier[tierKey] = value; break; }
    }
  }
  const est = estimateRawTier(byTier, tierKeyFor(wanted));
  return est ? { value: est.value, estimated: true, source: prefer, from: est.from } : null;
}

/** Condition key as `tierOf` writes it: "nm" -> "NEAR_MINT". */
function tierKeyFor(shortKey) {
  const map = {
    nm: 'NEAR_MINT', lp: 'LIGHTLY_PLAYED', mp: 'MODERATELY_PLAYED',
    hp: 'HEAVILY_PLAYED', dmg: 'DAMAGED',
  };
  return map[shortKey] || null;
}

// The vintage and modern price rules used to live here: a 12-month top-3 for
// vintage sets and a 3-month median for modern, selected by a user preference.
// They are gone rather than left unused. They were the second of two rules for
// the same question, and the card tile ran one while the card's own screen ran
// the other - $5.17 against $2.96 on the same Team Rocket Squirtle, both
// correct for what they were asked. `headlinePrice` in priceMath is the single
// rule now: the average of the last three sold, reports and excludes held out,
// which is what the ladder caption has always said it was doing.

export function isWithinCooldown(lastRefreshedAt) {
  if (!lastRefreshedAt) return false;
  const ts = new Date(lastRefreshedAt).getTime();
  return Date.now() - ts < COOLDOWN_MS;
}

const HOT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/**
 * Is a card one people keep coming back to? Same rule the nightly tiering
 * uses: three reads, one of them in the last week.
 */
export function isHotRow(row) {
  if (!row?.last_read_at) return false;
  const recent = Date.now() - new Date(row.last_read_at).getTime() < 7 * 24 * 60 * 60 * 1000;
  return (row.read_count || 0) >= 3 && recent;
}

/**
 * The manual refresh lock, by row. A week for most cards; a day for the ones
 * people check often, because those are the ones somebody wants live.
 */
export function isRefreshLocked(row) {
  if (!row?.last_refreshed_at) return false;
  const age = Date.now() - new Date(row.last_refreshed_at).getTime();
  return age < (isHotRow(row) ? HOT_COOLDOWN_MS : COOLDOWN_MS);
}

/**
 * Count a read. Fire and forget, batched, never awaited by a screen.
 *
 * Demand is what decides how often a card is refreshed, and this is the whole
 * measurement: a card that is opened is a card someone cares about the price
 * of. The grid is deliberately not counted; loading a collection is not
 * looking at a card.
 */
export function bumpPriceReads(rows) {
  const ids = [...new Set((rows || []).map((r) => r?.id).filter(Boolean))];
  if (ids.length === 0) return;
  supabase.rpc('bump_price_reads', { p_ids: ids }).then(({ error }) => {
    if (error) console.warn('[PriceCache] read count not recorded:', error.message);
  }).catch(() => {});
}

/**
 * Human-readable "X days ago" string.
 */
export function daysSinceRefresh(lastRefreshedAt) {
  if (!lastRefreshedAt) return 'never';
  const ms = Date.now() - new Date(lastRefreshedAt).getTime();
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

// ---- Write Functions ----

/**
 * Upsert one row into card_prices with Countdown data.
 */
export async function upsertCardPrice({
  cardName, setName, setNumber, variant,
  evidence, stats, isGraded, searchQuery, clearFailure = true, asks,
}) {
  if (!cardName) return;

  const row = {
    card_name: cardName,
    set_name: setName || '',
    set_number: setNumber || '',
    variant: (variant || '').toLowerCase().trim(),
    last_refreshed_at: new Date().toISOString(),
    // Whatever the nightly tiering marked, this write is the refresh it asked for.
    refresh_due: false,
  };

  // Only a lookup that actually produced a price for this card retires the
  // backoff. Writing a row full of tiers this card cannot use is not a success.
  if (clearFailure) {
    row.last_failure_stage = null;
    row.last_failure_at = null;
    row.failure_count = 0;
    row.retry_after = null;
  }

  if (evidence !== undefined) row.evidence = evidence;
  if (stats !== undefined) row.stats = stats;

  // The number the grid shows, worked out here rather than on every phone that
  // later opens the collection. Sales changed, so the headline changes with
  // them; recomputed on the same write, never separately, so the two cannot
  // drift apart.
  if (evidence !== undefined) {
    row.headline = computeHeadlineMap(evidence, asks, stats);
    row.priced_at = row.last_refreshed_at;
  }
  if (isGraded !== undefined) row.is_graded = isGraded;
  if (searchQuery !== undefined) row.search_query = searchQuery;

  console.log('[PriceCache] Upserting:', cardName, '|', setName, '|', setNumber, '|', row.variant,
    `evidence:${(evidence || []).length}`, `graded:${isGraded ? 'yes' : 'no'}`);

  const { error } = await supabase
    .from('card_prices')
    .upsert(row, { onConflict: 'card_name,set_name,set_number,variant' });

  if (error) {
    console.error('[PriceCache] Upsert error:', error);
  } else {
    console.log('[PriceCache] Upsert success:', cardName);
  }
}

// ---- Fetch + Cache (Single Card) ----

/**
 * Fetch and cache pricing for a single card. Fire-and-forget friendly.
 */
export async function fetchAndCacheSingleCard({ name, setName, setNumber, variant, grade, reportCard, tiers }) {
  if (!name) return;

  const identity = {
    cardName: name,
    setName: setName || '',
    setNumber: setNumber || '',
    variant: variant || '',
  };

  // The vendor layer knows which gate rejected the card. Keep the first stage
  // it reports: later ones are consequences of the first.
  let stage = null;
  const onFailure = (failedAt) => { if (!stage) stage = failedAt; };

  try {
    let stats = null;
    let evidence = [];
    let searchQuery = '';

    if (isGradedCard(grade)) {
      const result = await fetchGradedPricing({
        name,
        setName,
        number: setNumber,
        condition: grade,
        variant,
        reportCard,
        onFailure,
      });
      if (result?.pricingData) {
        stats = result.pricingData.stats || {};
        evidence = result.pricingData.evidence || [];
        searchQuery = result.pricingData.searchQuery || '';
      }
    } else {
      const pricing = await fetchPricing({
        name,
        setName,
        localId: setNumber,
        variant,
        reportCard,
        onFailure,
        // Already resolved by whoever drew the ladder, when they had it.
        tiers,
      });
      if (pricing) {
        stats = pricing.stats || {};
        evidence = pricing.evidence || [];
        searchQuery = pricing.searchQuery || '';
      }
    }

    // Did any of that actually produce a number for the card in hand? A record
    // with tiers this card cannot use counts as a failure, not a success.
    const priced = stats != null
      && getPriceForCard(stats, grade || 'NM', setName, null, evidence) != null;

    if (stats != null) {
      await upsertCardPrice({
        ...identity,
        evidence,
        stats,
        isGraded: isGradedCard(grade),
        searchQuery,
        clearFailure: priced,
      });
    }

    if (!priced) {
      // Tier averages with no sales behind them is its own failure, and until
      // now it was invisible: the cache saw a tier, called the card priced, and
      // logged nothing. Seven cards in one session were in that state, and the
      // repair loop never heard about any of them. An average cannot exist
      // without sales, so the aggregate call worked and the listings call came
      // back empty - which is a different problem from not finding the card,
      // and worth naming separately so the loop can act on it.
      const noListings = stats != null && Object.keys(stats).length > 0 && evidence.length === 0;
      await recordPricingFailure({
        ...identity,
        stage: stage || (noListings ? FAILURE_STAGES.NO_LISTINGS : FAILURE_STAGES.EMPTY_TIERS),
      });
    }
  } catch (e) {
    console.error('[PriceCache] Single card fetch error:', name, e.message);
    await recordPricingFailure({
      ...identity,
      stage: stage || FAILURE_STAGES.SEARCH_ERROR,
    });
  }
}

// ---- Batch Operations ----

/**
 * Refresh all collection prices via Countdown.
 * @param {Array} cards - [{id, name, set_name, set_number, grade, variant}]
 * @param {Function} onProgress - (completed, total) => void
 * @returns {{updated: number, errors: number}}
 */
export async function refreshCollectionPrices(cards, onProgress, onBatch) {
  if (!cards || cards.length === 0) return { updated: 0, errors: 0 };

  // Deduplicate by name+set_name+set_number+variant
  const seen = new Set();
  const unique = [];
  for (const card of cards) {
    const v = (card.variant || '').toLowerCase().trim();
    const key = `${card.name}||${card.set_name || ''}||${card.set_number || ''}||${v}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(card);
    }
  }

  const total = unique.length;
  let completed = 0;
  let updated = 0;
  let errors = 0;

  // Fetch in batches of 5 (Countdown is slower than JustTCG, be gentle)
  const BATCH_SIZE = 5;
  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    const batch = unique.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(async (card) => {
        try {
          await fetchAndCacheSingleCard({
            name: card.name,
            setName: card.set_name,
            setNumber: card.set_number,
            variant: card.variant,
            grade: card.grade,
            // Identity for the repair loop, so a failure arrives knowing which
            // card and which set it came from.
            reportCard: card,
          });
          return true;
        } catch (e) {
          console.error('[PriceCache] Batch fetch error:', card.name, e.message);
          return false;
        }
      })
    );
    completed += batch.length;
    updated += results.filter(Boolean).length;
    errors += results.filter(r => !r).length;
    if (onProgress) onProgress(completed, total);
    // Let the screen paint this batch rather than waiting for the whole run.
    if (onBatch) await onBatch(batch);
  }

  return { updated, errors };
}

/**
 * Auto-fetch pricing only for cards with no existing card_prices row.
 */
export async function autoFetchMissingPrices(cards, onProgress, known, onBatch) {
  if (!cards || cards.length === 0) return { updated: 0, errors: 0 };

  // The caller has almost always just read these rows to paint the screen.
  // Reading them again was a second round trip on every open for nothing.
  const existing = known || await getBulkCardPrices(cards);
  const due = cards.filter(c => {
    const hit = existing.get(c.id);
    return needsPricing(hit?.row, hit?.price);
  });

  if (due.length === 0) return { updated: 0, errors: 0 };

  console.log('[PriceCache] Auto-fetch:', due.length, 'of', cards.length, 'cards are due');
  return refreshCollectionPrices(due, onProgress, onBatch);
}

// Re-export for backwards compatibility
export { conditionToKey, isGradedCard };
